import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Search, CheckCircle, Truck, Clock, X, Trash2, Printer } from 'lucide-react';
import BottomNavigation from '../../components/common/BottomNavigation';
import { useApp } from '../../context/AppContext';
import { useOrder } from '../../context/OrderContext';
import { useData } from '../../context/DataContext';

const AdminOrders: React.FC = () => {
  const { setCurrentScreen } = useApp();
  const { orders, updateOrderStatus, deleteOrder } = useOrder();
  const { customers } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{
    show: boolean;
    orderId: string | null;
    orderInfo: string;
  }>({
    show: false,
    orderId: null,
    orderInfo: ''
  });

  const handleBack = () => {
    setCurrentScreen('admin-dashboard');
  };

  const handleStatusUpdate = async (orderId: string, newStatus: string) => {
    await updateOrderStatus(orderId, newStatus as any);
  };


  const handleDeleteClick = (orderId: string, customerName: string, orderAmount: number) => {
    setDeleteConfirm({
      show: true,
      orderId,
      orderInfo: `${customerName} - ₹${orderAmount}`
    });
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirm.orderId) {
      await deleteOrder(deleteConfirm.orderId);
      setDeleteConfirm({
        show: false,
        orderId: null,
        orderInfo: ''
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({
      show: false,
      orderId: null,
      orderInfo: ''
    });
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const currentDate = new Date().toLocaleDateString();
    const currentTime = new Date().toLocaleTimeString();
    
    // Get customer data for each order
    const ordersWithCustomerData = filteredOrders.map(order => {
      const customer = customers.find(c => c.customerId === order.customerId);
      return {
        ...order,
        customerData: customer || null
      };
    });

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Order History Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; margin-bottom: 30px; }
          .header h1 { color: #333; margin-bottom: 10px; }
          .header p { color: #666; margin: 5px 0; }
          .summary { background: #f5f5f5; padding: 15px; border-radius: 5px; margin-bottom: 20px; }
          .summary h3 { margin-top: 0; color: #333; }
          .order-card { border: 1px solid #ddd; margin-bottom: 20px; padding: 15px; border-radius: 5px; }
          .order-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
          .order-id { font-weight: bold; color: #333; }
          .order-status { padding: 4px 8px; border-radius: 3px; font-size: 12px; }
          .status-delivered { background: #d4edda; color: #155724; }
          .status-shipped { background: #cce7ff; color: #004085; }
          .status-pending { background: #fff3cd; color: #856404; }
          .status-cancelled { background: #f8d7da; color: #721c24; }
          .customer-info { background: #f8f9fa; padding: 10px; border-radius: 3px; margin: 10px 0; }
          .customer-info h4 { margin: 0 0 5px 0; color: #333; }
          .customer-details { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; font-size: 14px; }
          .order-items { margin: 10px 0; }
          .order-items h4 { margin: 0 0 10px 0; color: #333; }
          .item { display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #eee; }
          .item:last-child { border-bottom: none; }
          .order-total { text-align: right; font-weight: bold; font-size: 16px; margin-top: 10px; padding-top: 10px; border-top: 2px solid #333; }
          .no-orders { text-align: center; color: #666; padding: 40px; }
          @media print {
            body { margin: 0; }
            .order-card { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Order History Report</h1>
          <p>Generated on: ${currentDate} at ${currentTime}</p>
          <p>Total Orders: ${filteredOrders.length}</p>
        </div>

        <div class="summary">
          <h3>Summary</h3>
          <p><strong>Total Orders:</strong> ${filteredOrders.length}</p>
          <p><strong>Total Revenue:</strong> ₹${filteredOrders.reduce((sum, order) => sum + order.amount, 0).toLocaleString()}</p>
          <p><strong>Orders by Status:</strong></p>
          <ul>
            ${['pending', 'shipped', 'delivered', 'cancelled'].map(status => {
              const count = filteredOrders.filter(order => order.status === status).length;
              return count > 0 ? `<li>${status.charAt(0).toUpperCase() + status.slice(1)}: ${count}</li>` : '';
            }).filter(Boolean).join('')}
          </ul>
        </div>

        ${filteredOrders.length === 0 ? 
          '<div class="no-orders">No orders found matching the search criteria.</div>' :
          ordersWithCustomerData.map(order => `
            <div class="order-card">
              <div class="order-header">
                <span class="order-id">Order #${order.id}</span>
                <span class="order-status status-${order.status}">${order.status.toUpperCase()}</span>
              </div>
              
              <div class="customer-info">
                <h4>Customer Information</h4>
                <div class="customer-details">
                  <div>
                    <strong>Name:</strong> ${order.customerName}<br>
                    <strong>Customer ID:</strong> ${order.customerId}<br>
                    <strong>Order Date:</strong> ${new Date(order.date).toLocaleDateString()}
                    ${order.tableNumber ? `<br><strong>Table:</strong> ${order.tableNumber} (${order.tableLocation})` : ''}
                  </div>
                  ${order.customerData ? `
                    <div>
                      <strong>Email:</strong> ${order.customerData.email}<br>
                      <strong>Phone:</strong> ${order.customerData.phone}<br>
                      <strong>Location:</strong> ${order.customerData.city}
                    </div>
                  ` : '<div><em>Customer details not available</em></div>'}
                </div>
              </div>

              <div class="order-items">
                <h4>Order Items</h4>
                ${order.items.map(item => `
                  <div class="item">
                    <span>${item.productName} x ${item.quantity}</span>
                    <span>₹${item.price.toFixed(2)} each</span>
                  </div>
                `).join('')}
              </div>

              <div class="order-total">
                Total Amount: ₹${order.amount.toLocaleString()}
              </div>
            </div>
          `).join('')
        }
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handlePrintSingleOrder = (order: any) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const currentDate = new Date().toLocaleDateString();
    const currentTime = new Date().toLocaleTimeString();
    
    // Get customer data for this order
    const customer = customers.find(c => c.customerId === order.customerId);

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Order Invoice - ${order.id}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
          .header h1 { color: #333; margin-bottom: 10px; }
          .header p { color: #666; margin: 5px 0; }
          .order-info { background: #f8f9fa; padding: 15px; border-radius: 5px; margin-bottom: 20px; }
          .order-info h3 { margin-top: 0; color: #333; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
          .info-section h4 { margin: 0 0 10px 0; color: #333; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
          .info-section p { margin: 5px 0; }
          .order-items { margin: 20px 0; }
          .order-items h3 { margin-bottom: 15px; color: #333; }
          .items-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          .items-table th, .items-table td { border: 1px solid #ddd; padding: 10px; text-align: left; }
          .items-table th { background: #f5f5f5; font-weight: bold; }
          .items-table .text-right { text-align: right; }
          .order-total { text-align: right; font-weight: bold; font-size: 18px; margin-top: 20px; padding: 15px; background: #f8f9fa; border-radius: 5px; }
          .status-badge { display: inline-block; padding: 6px 12px; border-radius: 4px; font-size: 12px; font-weight: bold; }
          .status-delivered { background: #d4edda; color: #155724; }
          .status-shipped { background: #cce7ff; color: #004085; }
          .status-pending { background: #fff3cd; color: #856404; }
          .status-cancelled { background: #f8d7da; color: #721c24; }
          @media print {
            body { margin: 0; }
            .header { page-break-after: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Order Invoice</h1>
          <p>Order #${order.id}</p>
          <p>Generated on: ${currentDate} at ${currentTime}</p>
        </div>

        <div class="order-info">
          <h3>Order Information</h3>
          <div class="info-grid">
            <div class="info-section">
              <h4>Order Details</h4>
              <p><strong>Order ID:</strong> ${order.id}</p>
              <p><strong>Order Date:</strong> ${new Date(order.date).toLocaleDateString()}</p>
              <p><strong>Status:</strong> <span class="status-badge status-${order.status}">${order.status.toUpperCase()}</span></p>
            </div>
            <div class="info-section">
              <h4>Customer Information</h4>
              <p><strong>Name:</strong> ${order.customerName}</p>
              <p><strong>Customer ID:</strong> ${order.customerId}</p>
              ${customer ? `
                <p><strong>Email:</strong> ${customer.email}</p>
                <p><strong>Phone:</strong> ${customer.phone}</p>
                <p><strong>Location:</strong> ${customer.city}</p>
              ` : '<p><em>Customer details not available</em></p>'}
            </div>
          </div>
        </div>

        <div class="order-items">
          <h3>Order Items</h3>
          <table class="items-table">
            <thead>
              <tr>
                <th>Product Name</th>
                <th>Quantity</th>
                <th class="text-right">Unit Price</th>
                <th class="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              ${order.items.map((item: any) => `
                <tr>
                  <td>${item.productName}</td>
                  <td>${item.quantity}</td>
                  <td class="text-right">₹${item.price.toFixed(2)}</td>
                  <td class="text-right">₹${(item.quantity * item.price).toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        <div class="order-total">
          Total Amount: ₹${order.amount.toLocaleString()}
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered':
        return <CheckCircle size={16} className="text-green-400" />;
      case 'shipped':
        return <Truck size={16} className="text-blue-400" />;
      case 'pending':
        return <Clock size={16} className="text-yellow-400" />;
      case 'cancelled':
        return <X size={16} className="text-red-400" />;
      default:
        return <Clock size={16} className="text-slate-400" />;
    }
  };

  const getNextStatus = (currentStatus: string) => {
    switch (currentStatus) {
      case 'pending':
        return 'shipped'; // Preparing -> Ready
      case 'shipped':
        return 'delivered'; // Ready -> Served
      default:
        return currentStatus;
    }
  };

  const getStatusButtonText = (currentStatus: string) => {
    switch (currentStatus) {
      case 'pending':
        return 'Mark as Ready';
      case 'shipped':
        return 'Mark as Served';
      default:
        return 'Update Status';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'Served';
      case 'shipped':
        return 'Ready';
      case 'pending':
        return 'Preparing';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const filteredOrders = orders
    .filter(order => {
      if (searchTerm === '') return true;
      
      return order.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
             order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
             order.customerId.toLowerCase().includes(searchTerm.toLowerCase());
    })
    .sort((a, b) => {
      // Sort by date in descending order (newest first)
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      
      console.log(`Sorting: Order ${a.id} (${a.date}) vs Order ${b.id} (${b.date})`);
      
      // If dates are the same, sort by order ID as secondary criteria
      if (dateA === dateB) {
        return b.id.localeCompare(a.id);
      }
      
      return dateB - dateA;
    });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-900 text-green-400';
      case 'shipped':
        return 'bg-blue-900 text-blue-400';
      case 'pending':
        return 'bg-yellow-900 text-yellow-400';
      case 'cancelled':
        return 'bg-red-900 text-red-400';
      default:
        return 'bg-slate-900 text-slate-400';
    }
  };

  const OrderItem: React.FC<{ order: any; index: number }> = ({ order, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="p-4 bg-slate-800 rounded-lg border border-slate-700"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center">
            <span className="text-white font-semibold text-sm">
              {order.customerName.charAt(0)}
            </span>
          </div>
          <div>
            <h3 className="text-white font-medium">{order.customerName}</h3>
            <p className="text-slate-400 text-sm">Order ID: {order.id || 'N/A'}</p>
            <p className="text-slate-400 text-sm">Customer ID: {order.customerId}</p>
            {order.tableNumber && (
              <p className="text-slate-400 text-sm">🪑 Table: {order.tableNumber} ({order.tableLocation})</p>
            )}
          </div>
        </div>
        <div className="text-right">
          <p className="text-white font-semibold">₹{order.amount.toLocaleString()}</p>
          <div className="flex items-center space-x-1">
            {getStatusIcon(order.status)}
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
              {getStatusLabel(order.status)}
            </span>
          </div>
        </div>
      </div>
      
      {/* Order Items */}
      <div className="mt-3 pt-3 border-t border-slate-700">
        <h4 className="text-white font-medium mb-2 text-sm">Order Items:</h4>
        <div className="space-y-1">
          {order.items.map((item: any, index: number) => (
            <div key={index} className="flex justify-between text-sm">
              <span className="text-slate-300">{item.productName} x {item.quantity}</span>
              <span className="text-slate-400">₹{(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Order Actions */}
      <div className="flex space-x-2 mt-3">
        {order.status !== 'delivered' && order.status !== 'cancelled' && (
          <motion.button
            onClick={() => handleStatusUpdate(order.id, getNextStatus(order.status))}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex-1 bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium py-2 px-3 rounded-lg transition-colors duration-200"
          >
            {getStatusButtonText(order.status)}
          </motion.button>
        )}
        <motion.button
          onClick={() => handlePrintSingleOrder(order)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="px-3 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium py-2 rounded-lg transition-colors duration-200 flex items-center space-x-1"
        >
          <Printer size={14} />
          <span>Print</span>
        </motion.button>
        <motion.button
          onClick={() => handleDeleteClick(order.id, order.customerName, order.amount)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="px-3 bg-red-600 hover:bg-red-700 text-white text-sm font-medium py-2 rounded-lg transition-colors duration-200 flex items-center space-x-1"
        >
          <Trash2 size={14} />
          <span>Delete</span>
        </motion.button>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-slate-900 pb-20">
      {/* Header */}
      <div className="flex items-center justify-between p-6 bg-slate-800 border-b border-slate-700">
        <div className="flex items-center">
          <button
            onClick={handleBack}
            className="p-2 text-slate-400 hover:text-white mr-4"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-xl font-bold text-white">Order History</h1>
        </div>
        <div className="flex items-center space-x-3">
          <motion.button
            onClick={handlePrint}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center space-x-2 bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
          >
            <Printer size={18} />
            <span>Print Report</span>
          </motion.button>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
            <input
              type="text"
              placeholder="Search orders by customer name, order ID, or customer ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-slate-800 border border-slate-600 rounded-lg pl-10 pr-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm md:text-base min-h-[48px]"
            />
          </div>
        </motion.div>

        {/* Orders List */}
        <div className="space-y-3">
          {orders.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-12"
            >
              <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">No Orders Yet</h3>
              <p className="text-slate-400 text-sm">
                Customer orders will appear here once they start placing orders.
              </p>
            </motion.div>
          ) : filteredOrders.length > 0 ? (
            filteredOrders.map((order, index) => (
              <OrderItem key={order.id} order={order} index={index} />
            ))
          ) : (
            <div className="text-center py-8">
              <p className="text-slate-400">No orders found matching your filters</p>
            </div>
          )}
        </div>
      </div>

      <BottomNavigation />

      {/* Delete Confirmation Dialog */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-slate-800 rounded-xl p-6 max-w-md w-full border border-slate-700"
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                <Trash2 size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Delete Order</h3>
                <p className="text-slate-400 text-sm">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-slate-300 mb-2">
                Are you sure you want to delete this order?
              </p>
              <div className="bg-slate-700 rounded-lg p-3">
                <p className="text-white font-medium">{deleteConfirm.orderInfo}</p>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <motion.button
                onClick={handleDeleteCancel}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 bg-slate-600 hover:bg-slate-500 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
              >
                Cancel
              </motion.button>
              <motion.button
                onClick={handleDeleteConfirm}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
              >
                Delete Order
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
